import type { BackcountryMetadata } from "~/lib/api";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { Input } from "~/components/ui/input";
import { useInlineEditor } from "~/hooks/useInlineEditor";

type Duration = NonNullable<BackcountryMetadata["duration"]>;

interface DurationEditorProps {
  value: Duration | null | undefined;
  onChange: (value: Duration | null) => void;
  canEdit?: boolean;
  disabled?: boolean;
}

export function DurationEditor({ value, onChange, canEdit, disabled }: DurationEditorProps) {
  const {
    editing,
    saving,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  } = useInlineEditor<Duration>({
    value,
    onSave: onChange,
    toEditState: (v) => ({
      min: v?.min?.toString() ?? "",
      max: v?.max?.toString() ?? "",
    }),
    fromEditState: (state) => {
      const min = parseFloat(state.min);
      const max = parseFloat(state.max);
      const hasMin = state.min.trim() && !isNaN(min) && min > 0;
      const hasMax = state.max.trim() && !isNaN(max) && max > 0;

      if (!hasMin && !hasMax) {
        return null;
      }

      // If only one value provided, use it for both
      const minVal = hasMin ? min : max;
      const maxVal = hasMax ? max : min;

      return {
        min: Math.min(minVal, maxVal),
        max: Math.max(minVal, maxVal),
        unit: "hours" as const,
      };
    },
  });

  if (editing) {
    return (
      <div ref={containerRef} className="flex flex-wrap items-center gap-2" onBlur={handleBlur}>
        <Input
          ref={primaryInputRef}
          type="number"
          min="0"
          step="0.5"
          value={editState.min}
          onChange={(e) => setField("min", e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Min"
          disabled={saving}
          className="h-8 w-20 text-sm"
          aria-label="Minimum hours"
        />
        <span className="text-xs text-muted-foreground">–</span>
        <Input
          type="number"
          min="0"
          step="0.5"
          value={editState.max}
          onChange={(e) => setField("max", e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Max"
          disabled={saving}
          className="h-8 w-20 text-sm"
          aria-label="Maximum hours"
        />
        <span className="text-xs text-muted-foreground">hours</span>
        <Button size="icon-sm" variant="ghost" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={saving}>
          <Icon i="check" className="size-6 text-primary" />
        </Button>
      </div>
    );
  }

  if (value) {
    const display = <DurationDisplay duration={value} />;

    if (canEdit) {
      return (
        <button
          type="button"
          onClick={startEditing}
          disabled={disabled}
          className="text-foreground transition-colors hover:text-primary disabled:opacity-50"
        >
          {display}
        </button>
      );
    }

    return display;
  }

  if (canEdit) {
    return (
      <button
        onClick={startEditing}
        disabled={disabled}
        className="cursor-pointer text-sm text-muted-foreground transition-colors hover:text-primary"
      >
        + Add duration
      </button>
    );
  }

  return null;
}

interface DurationDisplayProps {
  duration: Duration;
  className?: string;
}

export function DurationDisplay({ duration, className }: DurationDisplayProps) {
  const { min, max } = duration;

  if (min === max) {
    return <span className={cn("text-sm", className)}>{min}h</span>;
  }

  return (
    <span className={cn("text-sm", className)}>
      {min}-{max}h
    </span>
  );
}
